import React from 'react';
import { supabase } from './supabaseClient';
import { t } from './translations';

/**
 * Standardgräns för maximalt antal AI-sammanfattningar per månad för betalande användare (Basic).
 */
export const MONTHLY_SUMMARY_LIMIT = 775;

/**
 * Formaterar och renderar AI-genererade sammanfattningar med stöd för
 * rubriker (##), listpunkter (- eller *) och fetstil (**text**).
 *
 * @param {string} text - Den råa texten som ska renderas.
 * @param {string} [language='English'] - Språkinställning (för framtida lokalanpassning).
 * @returns {React.ReactNode} De formaterade React-elementen.
 */
export const renderAISummary = (text, language = 'English') => {
  if (!text) {
    return <span className="text-slate-400 italic">{t(language, 'summary_no_text')}</span>;
  }

  // Hjälpfunktion för att ersätta **fetstil** med <strong>-taggar
  const renderBoldText = (str) => {
    const parts = str.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} className="text-slate-900 font-bold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  return text.split('\n').map((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('##')) {
      return (
        <h2
          key={idx}
          className="text-xl font-bold text-slate-900 mt-6 mb-2 font-sans border-b pb-2"
        >
          {trimmed.replace('##', '').trim()}
        </h2>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      return (
        <li key={idx} className="ml-5 list-disc mb-1">
          {renderBoldText(trimmed.substring(2))}
        </li>
      );
    } else if (trimmed === '') {
      return <div key={idx} className="h-2"></div>;
    } else {
      return <div key={idx} className="mb-2">{renderBoldText(trimmed)}</div>;
    }
  });
};

/**
 * Återskapar en fullständig artikeltext utifrån ett inverterat index-objekt från OpenAlex.
 *
 * @param {Object} invertedIndex - Objekt med ord som nycklar och positioner i arrayer.
 * @returns {string|null} Den återskapade textsträngen eller null.
 */
export const reconstructAbstract = (invertedIndex) => {
  if (!invertedIndex) return null;
  const sorted = Object.entries(invertedIndex)
    .flatMap(([word, positions]) => positions.map((pos) => ({ word, pos })))
    .sort((a, b) => a.pos - b.pos);
  return sorted.map((item) => item.word).join(' ');
};

// ============================================================================
// KVOTHANTERING & RÄKNARE FÖR AI-SAMMANFATTNINGAR
// ============================================================================

/**
 * Kontrollerar om en användare har nått maxgränsen för sammanfattningar denna månad.
 *
 * @param {Object} profile - Användarprofilen som hämtats från Supabase (user_profiles).
 * @param {number} [limit=MONTHLY_SUMMARY_LIMIT] - Maxgränsen (standard: 775).
 * @returns {boolean} True om gränsen har uppnåtts eller överskridits, annars false.
 */
export const hasReachedSummaryLimit = (profile, limit = MONTHLY_SUMMARY_LIMIT) => {
  if (!profile) {
    return true; // Spärra säkert om profil saknas
  }
  const count = profile.summaries_generated_this_month || 0;
  return count >= limit;
};

/**
 * Registrerar och räknar upp en använd sammanfattning i Supabase med 1.
 * Anropar den atomära PostgreSQL-funktionen 'increment_summary_count'.
 *
 * @param {string} userId - Användarens unika UUID.
 * @returns {Promise<{success: boolean, count: number|null}>} Objekt med resultat och nytt saldo.
 */
export const recordSummaryUsage = async (userId) => {
  if (!userId) {
    console.warn('⚠️ recordSummaryUsage: Inget giltigt userId angavs.');
    return { success: false, count: null };
  }

  try {
    const { data: newCount, error } = await supabase.rpc('increment_summary_count', {
      target_user_id: userId,
    });

    if (error) {
      console.error('❌ Fel vid anrop till increment_summary_count i Supabase:', error.message);
      return { success: false, count: null };
    }

    return { success: true, count: newCount };
  } catch (err) {
    console.error('❌ Oväntat nätverksfel vid uppräkning av sammanfattning:', err);
    return { success: false, count: null };
  }
};

// ============================================================================
// STRIPE PRENUMERATION & HYBRID KUNDPORTAL
// ============================================================================

/**
 * Returnerar standardpaket och harmoniserad prisinformation för gränssnittet.
 *
 * @returns {Promise<{availablePackages: Array<Object>, monthly: Object}>}
 */
export const fetchOfferings = async () => {
  const defaultPackage = {
    identifier: '$rc_monthly',
    packageType: 'MONTHLY',
    product: {
      identifier: 'basic_monthly_25sek',
      description: 'Science Monitor Basic - 5 bevakningar, max 25 summeringar/dygn',
      title: 'Science Monitor Basic',
      price: 25.00,
      priceString: '25,00 kr',
    },
  };

  return {
    availablePackages: [defaultPackage],
    monthly: defaultPackage,
  };
};

/**
 * Initialiserar betalningsmodulen vid sidstart utan att logga personuppgifter.
 *
 * @param {string} userId - Användarens unika ID.
 * @param {string|null} [userEmail=null] - Användarens e-postadress.
 */
export const initializeRevenueCat = async (userId, userEmail = null) => {
  try {
    await fetchOfferings();
  } catch (err) {
    console.error('Fel vid initial hämtning av erbjudande:', err);
  }
};

/**
 * Initierar en Stripe Checkout Session för en återkommande prenumeration
 * och omdirigerar användaren direkt till Stripes officiella kassa.
 *
 * @param {string|null} [priceId=null] - Valfritt Stripe Price ID. Om null används backend-konfigurationen (STRIPE_PRICE_ID).
 * @returns {Promise<boolean>} True om omdirigeringen startade, annars false.
 */
export const purchaseMonthlyPackage = async (priceId = null, language = 'English') => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      alert(t(language, 'checkout_login_required'));
      return false;
    }

    const payload = priceId ? { priceId } : {};

    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Fel från /api/create-checkout-session:', data.error);
      alert(data.error || t(language, 'checkout_failed'));
      return false;
    }

    if (data.url) {
      window.location.href = data.url;
      return true;
    }

    return false;
  } catch (err) {
    console.error('Nätverksfel vid anrop till Stripe Checkout:', err);
    alert(`${t(language, 'error_generic')} ${err.message}`);
    return false;
  }
};

/**
 * Hämtar direktlänk till Stripe Customer Portal för att hantera betalmetoder eller säga upp prenumeration.
 *
 * @returns {Promise<string|null>} URL till kundportalen eller null vid fel.
 */
export const getCustomerPortalUrl = async () => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      console.warn('Ingen inloggad session hittades vid hämtning av kundportal.');
      return null;
    }

    const response = await fetch('/api/create-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Fel vid hämtning av portalsession:', data.error);
      return null;
    }

    return data.url;
  } catch (error) {
    console.error('Nätverksfel vid anrop till /api/create-portal-session:', error);
    return null;
  }
};
